import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CryptoError, decodeLinkKey, fromBase64Url, openSecret } from '@sss/crypto';
import type { SecretMetadata } from '@sss/types';
import FlameMark from '../components/Brand/FlameMark';
import { usePageTitle } from '../hooks/usePageTitle';
import CopyButton from '../components/Secret/CopyButton';
import secretStyles from '../components/Secret/Secret.module.css';
import { forgetFragment, readFragment } from '../lib/secretLinks';
import { SecretsApiError, getSecret, revealSecret } from '../lib/secretsApi';
import { formatTimeLeft } from '../lib/timeLeft';
import ui from '../styles/ui.module.css';
import styles from './RevealPage.module.css';

/**
 * /s/:id#<linkKey> — click to reveal. Loading this page (as link-preview
 * bots do) only fetches metadata; a view is used only when someone presses
 * the button. The fetched payload stays in memory so a mistyped passphrase
 * can be retried without fetching (and burning) again.
 */

type State =
  | { step: 'checking' }
  | { step: 'incomplete' }
  | { step: 'gone' }
  | { step: 'ready'; meta: SecretMetadata }
  | { step: 'revealing'; meta: SecretMetadata }
  | { step: 'locked'; payload: Uint8Array; viewsLeft: number; wrong: boolean }
  | { step: 'revealed'; text: string; viewsLeft: number }
  | { step: 'failed'; reason: 'network' | 'rateLimited' | 'undecryptable' | 'other' };

export default function RevealPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ step: 'checking' });
  const [passphrase, setPassphrase] = useState('');
  // The key is read once from the fragment and kept only in memory.
  const linkKey = useRef<Uint8Array | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  usePageTitle(t('pageTitle.reveal'));

  useEffect(() => {
    const fragment = readFragment(window.location.hash);
    try {
      linkKey.current = fragment ? decodeLinkKey(fragment) : null;
    } catch {
      linkKey.current = null;
    }
    if (!linkKey.current) {
      setState({ step: 'incomplete' });
      return;
    }
    let cancelled = false;
    getSecret(id)
      .then((meta) => !cancelled && setState({ step: 'ready', meta }))
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof SecretsApiError && err.type === 'gone') {
          forgetFragment();
          setState({ step: 'gone' });
        } else setState({ step: 'failed', reason: err instanceof SecretsApiError && err.type === 'rateLimited' ? 'rateLimited' : 'network' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (state.step === 'revealed' || state.step === 'gone' || state.step === 'failed') heading.current?.focus();
  }, [state.step]);

  async function open(payload: Uint8Array, viewsLeft: number, hasPassphrase: boolean) {
    const key = linkKey.current;
    if (!key) return;
    try {
      const text = await openSecret(payload, key, { passphrase: passphrase || undefined });
      linkKey.current = null;
      forgetFragment();
      setPassphrase('');
      setState({ step: 'revealed', text, viewsLeft });
    } catch (err) {
      if (err instanceof CryptoError && (err.code === 'passphraseRequired' || (err.code === 'decryptionFailed' && hasPassphrase))) {
        setState({ step: 'locked', payload, viewsLeft, wrong: err.code === 'decryptionFailed' });
      } else {
        forgetFragment();
        setState({ step: 'failed', reason: 'undecryptable' });
      }
    }
  }

  async function onReveal(e: React.FormEvent, meta: SecretMetadata) {
    e.preventDefault();
    setState({ step: 'revealing', meta });
    try {
      const result = await revealSecret(id);
      await open(fromBase64Url(result.payload), result.viewsLeft, meta.hasPassphrase);
    } catch (err) {
      if (err instanceof SecretsApiError && err.type === 'gone') {
        forgetFragment();
        setState({ step: 'gone' });
      } else {
        const type = err instanceof SecretsApiError ? err.type : 'other';
        setState({ step: 'failed', reason: type === 'rateLimited' || type === 'network' ? type : 'other' });
      }
    }
  }

  async function onUnlock(e: React.FormEvent, payload: Uint8Array, viewsLeft: number) {
    e.preventDefault();
    await open(payload, viewsLeft, true);
  }

  const title = (
    <h1 className={styles.title} ref={heading} tabIndex={-1}>
      {t('reveal.titleLine1')} <span className={styles.accent}>{t('reveal.titleLine2')}</span>
    </h1>
  );

  return (
    <main className={styles.page}>
      {state.step === 'checking' && (
        <p className={ui.hint} role="status">
          {t('reveal.checking')}
        </p>
      )}

      {state.step === 'incomplete' && (
        <Message heading={t('reveal.incomplete.title')} body={t('reveal.incomplete.body')} headingRef={heading} />
      )}

      {state.step === 'gone' && <Message heading={t('reveal.gone.title')} body={t('reveal.gone.body')} headingRef={heading} />}

      {state.step === 'failed' && (
        <Message heading={t('reveal.failed.title')} body={t(`reveal.failed.${state.reason}`)} headingRef={heading} />
      )}

      {(state.step === 'ready' || state.step === 'revealing') && (
        <form className={styles.stack} onSubmit={(e) => onReveal(e, state.meta)} aria-busy={state.step === 'revealing'}>
          {title}
          <p className={styles.lead}>{t('reveal.lead', { count: state.meta.viewsLeft })}</p>
          <div className={secretStyles.fuse}>
            <div className={`${secretStyles.fuseRow} ${ui.eyebrow}`}>
              <span>{t('reveal.fuse')}</span>
              <span>{formatTimeLeft(Date.parse(state.meta.expiresAt) - Date.now(), i18n.language)}</span>
            </div>
            <strong>{t('reveal.viewsLeft', { count: state.meta.viewsLeft })}</strong>
          </div>
          {state.meta.hasPassphrase && (
            <label className={ui.field}>
              {t('reveal.passphraseLabel')}
              <input
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder={t('reveal.passphrasePlaceholder')}
                autoComplete="off"
                required
              />
            </label>
          )}
          <button type="submit" className={ui.button} disabled={state.step === 'revealing' || (state.meta.hasPassphrase && !passphrase)}>
            <FlameMark size={18} tone="onAccent" />
            {state.step === 'revealing' ? t('reveal.revealing') : t('reveal.button')}
          </button>
          <p className={ui.hint}>{t('reveal.botNote')}</p>
        </form>
      )}

      {state.step === 'locked' && (
        <form className={styles.stack} onSubmit={(e) => onUnlock(e, state.payload, state.viewsLeft)}>
          <h1 className={styles.title} ref={heading} tabIndex={-1}>
            {t('reveal.locked.title')}
          </h1>
          {state.wrong && (
            <p role="alert" className={ui.error}>
              {t('reveal.locked.wrong')}
            </p>
          )}
          <p className={secretStyles.notice}>{t('reveal.locked.keepTab')}</p>
          <label className={ui.field}>
            {t('reveal.passphraseLabel')}
            <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" autoFocus required />
          </label>
          <button type="submit" className={ui.button} disabled={!passphrase}>
            {t('reveal.locked.unlock')}
          </button>
        </form>
      )}

      {state.step === 'revealed' && (
        <section className={styles.stack} aria-labelledby="revealed-title">
          <h1 id="revealed-title" className={styles.title} ref={heading} tabIndex={-1}>
            {t('reveal.revealed.title')}
          </h1>
          {/* Scrolls when long, so it must take keyboard focus. */}
          <pre className={secretStyles.secretText} data-testid="secret-text" tabIndex={0} aria-labelledby="revealed-title">
            {state.text}
          </pre>
          <div className={secretStyles.actions}>
            <CopyButton value={state.text} className={ui.button} />
          </div>
          <p className={secretStyles.notice}>
            {state.viewsLeft === 0 ? t('reveal.revealed.burned') : t('reveal.revealed.viewsLeft', { count: state.viewsLeft })}
          </p>
        </section>
      )}
    </main>
  );
}

function Message({ heading, body, headingRef }: { heading: string; body: string; headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  return (
    <section className={styles.stack}>
      <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
        {heading}
      </h1>
      <p className={styles.lead}>{body}</p>
    </section>
  );
}
